import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DonationForm } from '@feathr/blackbox';
import { Button, CodeBlock, CopyToClipboardButton, Text } from '@feathr/components';
import { useSupportChat } from '@feathr/extender/hooks';
import { getInlineDonationFormDiv, getModalDonationFormHash } from '@feathr/extender/state';

import type { EmbedType } from '../DonationFormEmbedModal';

import * as styles from './EmbedFlow.css';

interface IEmbedFlowProps {
  donationForm: DonationForm;
  embedType: EmbedType;
  onCodeCopied?: (snippetId: string) => void;
}

function EmbedFlow({
  donationForm,
  embedType,
  onCodeCopied,
}: Readonly<IEmbedFlowProps>): JSX.Element {
  const { t } = useTranslation();
  const { openSupportChat } = useSupportChat();

  const formId = donationForm.id;

  return (
    <div className={styles.embedContent}>
      {embedType === 'inline' && (
        <>
          <Text.DefaultRegular className={styles.description}>
            {t('Add a div where you want the form to appear.')}
          </Text.DefaultRegular>
          <div className={styles.codeBlockWrapper}>
            <CodeBlock code={getInlineDonationFormDiv(formId)} language={'markup'} />
          </div>
          <CopyToClipboardButton
            name={'copy-div'}
            onMouseDown={(): void => {
              onCodeCopied?.('inline-div');
            }}
            t={t}
            text={getInlineDonationFormDiv(formId)}
            type={'secondary'}
          />
        </>
      )}

      {embedType === 'popup' && (
        <>
          <Text.DefaultRegular className={styles.description} truncate={false}>
            {t(
              'Add this anchor link as the destination URL on any button or link you want to launch the popup donation form.',
            )}
          </Text.DefaultRegular>
          <Text.DefaultRegular className={styles.urlDisplay}>
            {getModalDonationFormHash(formId)}
          </Text.DefaultRegular>
          <CopyToClipboardButton
            name={'copy-link'}
            onMouseDown={(): void => {
              onCodeCopied?.('popup-link');
            }}
            t={t}
            text={getModalDonationFormHash(formId)}
            type={'secondary'}
          />
        </>
      )}

      <div className={styles.helpSection}>
        <Text.DefaultRegular>{t('Still need help?')}</Text.DefaultRegular>
        <div className={styles.helpLinks}>
          <Button
            href={
              'https://help.feathr.co/hc/en-us/articles/35989545493271-How-to-Create-a-Fundraising-Form'
            }
            target={'_blank'}
            type={'link'}
          >
            {t('Read our full guide')}
          </Button>
          {t(' or ')}
          <Button onClick={openSupportChat} type={'link'}>
            {t('chat with our support team')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EmbedFlow;
